// src/utils/activeMedicines.js
//
// Which medicines a patient is still on, according to the SIGNED prescriptions
// Previu knows about. This is NOT a medication list: anything prescribed
// elsewhere, bought over the counter or written on a DRAFT is invisible here,
// and the UI says so. Pure: no React, no network.
//
// Rule: a medicine is active while  signedDay + durationDays >= today  (all
// local calendar days). A course signed 30 Oct for 5 days ends 4 Nov; it is
// active on 4 Nov and completed from 5 Nov. A row without a usable duration is
// "unknown" — never active, never claimed as completed.

import { localDay } from './consultVitals';

const pad = n => String(n).padStart(2, '0');
const dayOf = d => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const parseDay = day => { const [y, m, d] = day.split('-').map(Number); return new Date(y, m - 1, d); };

/** 'YYYY-MM-DD' for a Date or an existing day string; defaults to today. */
export function toDay(value = new Date()) {
  if (typeof value === 'string') return /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : localDay(value);
  return dayOf(value);
}

/** 'YYYY-MM-DD' `n` calendar days after `day`. */
export function addDays(day, n) {
  const d = parseDay(day);
  d.setDate(d.getDate() + n);
  return dayOf(d);
}

/** Short display date for a day string: "4 Nov". */
export function fmtDayShort(day) {
  if (!day) return '';
  return parseDay(day).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
}

/** Full display date for a day string: "30 Oct 2026". */
export function fmtDayFull(day) {
  if (!day) return '';
  return parseDay(day).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

const validDuration = n => Number.isFinite(Number(n)) && Number(n) > 0 && n !== null && n !== '';

export const isSigned = rx => String(rx?.status || '').toUpperCase() === 'SIGNED';

/** Last day a signed course is taken, or null when it cannot be worked out. */
export function courseEndDay(rx, item) {
  const signed = localDay(rx?.signedAt);
  if (!signed || !validDuration(item?.durationDays)) return null;
  return addDays(signed, Math.floor(Number(item.durationDays)));
}

/**
 * Status of one prescription row.
 * @returns {{kind: 'active'|'completed'|'unknown', endDay: string|null, label: string}}
 */
export function itemStatus(rx, item, today = new Date()) {
  const end = courseEndDay(rx, item);
  if (!end) return { kind: 'unknown', endDay: null, label: 'Duration not set' };
  if (end >= toDay(today)) return { kind: 'active', endDay: end, label: `ACTIVE · ends ${fmtDayShort(end)}` };
  return { kind: 'completed', endDay: end, label: 'Completed' };
}

const nameKey = item => String(item?.drugName || '').trim().toLowerCase();
export const medicineLabel = item => [item?.drugName, item?.strength].map(s => String(s || '').trim()).filter(Boolean).join(' ');

/**
 * Active medicines across all signed prescriptions, one entry per drug name
 * (the same drug on two prescriptions keeps the course that ends last).
 * @returns {{name: string, drugName: string, endDay: string, rxId: *, visitId: *}[]} soonest-ending first
 */
export function activeMedicines(prescriptions, today = new Date()) {
  const byName = new Map();
  (Array.isArray(prescriptions) ? prescriptions : []).filter(isSigned).forEach(rx => {
    (rx.items || []).forEach(item => {
      const key = nameKey(item);
      if (!key) return;
      const st = itemStatus(rx, item, today);
      if (st.kind !== 'active') return;
      const prev = byName.get(key);
      if (!prev || st.endDay > prev.endDay) {
        byName.set(key, { name: medicineLabel(item), drugName: item.drugName, endDay: st.endDay, rxId: rx.id, visitId: rx.visitId ?? null });
      }
    });
  });
  return [...byName.values()].sort((a, b) => a.endDay.localeCompare(b.endDay) || a.name.localeCompare(b.name));
}

/**
 * What the Active medicines tile shows.
 * @returns {{count: number, names: string[], more: number, earliestEnd: string|null}}
 */
export function activeMedicinesSummary(prescriptions, today = new Date(), maxNames = 3) {
  const list = activeMedicines(prescriptions, today);
  return {
    count: list.length,
    names: list.slice(0, maxNames).map(m => m.name),
    more: Math.max(0, list.length - maxNames),
    earliestEnd: list.length ? list[0].endDay : null,
  };
}
